import React, { useState, useEffect, useRef } from 'react';
import {
  X, ChevronRight, Check, Image as ImageIcon, AlertCircle,
  Sparkles, RotateCcw, Settings, Wand2, Globe, Users, FileText,
  ChevronDown, ChevronUp, RefreshCw, Eye, EyeOff, Zap, Lightbulb, UploadCloud
} from 'lucide-react';
import * as emailApi from '../../services/emailMarketingApi';
import { generateCampaignContent } from '../../services/emailMarketingApi';

// ─── Constants ────────────────────────────────────────────────────────────────
const CAMPAIGN_IMAGE_URL = "https://api.miplapp.in/storage/app/public/campaign-images/";
const BRAND_STORAGE_KEY  = 'reach_brand_settings_v2';

const DEFAULT_BRAND = {
  brandName:  '',
  websiteUrl: 'https://www.ottomed.com/',
  tone:       'professional',
};

const TONES = [
  { value: 'professional', label: 'Professional', emoji: '💼' },
  { value: 'friendly',     label: 'Friendly',     emoji: '🤝' },
  { value: 'urgent',       label: 'Urgent',       emoji: '⚡' },
  { value: 'informative',  label: 'Informative',  emoji: '📋' },
];

const CAMPAIGN_TYPES = [
  { label: 'Product Launch', icon: '🚀' },
  { label: 'Special Offer',  icon: '🎁' },
  { label: 'Newsletter',     icon: '📰' },
  { label: 'Event Invite',   icon: '📅' },
  { label: 'Follow-up',      icon: '🔁' },
  { label: 'Custom Brief',   icon: '✍️' },
];

// ─── Dynamic HTML Templates ───────────────────────────────────────────────────
const PREDEFINED_TEMPLATES = [
  {
    name: 'Default (Text)',
    icon: '📝',
    generateHtml: () => '' 
  },
  {
    name: 'Template 1',
    icon: '📱',
    generateHtml: (url) => `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; text-align: center; background-color: #f5f5f7; padding: 40px 20px; color: #1d1d1f; border-radius: 12px;">
  <h1 style="font-size: 28px; font-weight: 600; margin-bottom: 20px;">Now's the time<br>to pamper Mom.</h1>
  <a href="${url}" style="display: inline-block; background-color: #0071e3; color: #ffffff; text-decoration: none; padding: 8px 16px; border-radius: 20px; font-weight: 600; font-size: 14px; margin-bottom: 30px;">Shop Now</a>
  
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>
      <td width="50%" align="center" style="padding: 10px;">
        <div style="background: #ffffff; padding: 20px; border-radius: 18px; box-shadow: 0 4px 10px rgba(0,0,0,0.05);">
          <!-- HIGHLIGHT THE URL BELOW and click "Upload Image from Computer" -->
          <img src="https://placehold.co/150x200?text=Upload+Phone+Image" alt="Phone" style="max-width: 100%; height: auto;">
          <p style="color: #bf4800; font-size: 11px; font-weight: 600; margin: 10px 0 2px;">New</p>
          <h3 style="margin: 0 0 5px; font-size: 16px;">Smartphone 16</h3>
          <p style="font-size: 11px; color: #86868b;">From Rs.4992/mo.<br>or Rs.59900*</p>
          <a href="${url}" style="display: inline-block; background-color: #0071e3; color: #ffffff; text-decoration: none; padding: 6px 14px; border-radius: 14px; font-size: 12px; margin-top: 10px;">Buy</a>
        </div>
      </td>
      <td width="50%" align="center" style="padding: 10px;">
        <div style="background: #ffffff; padding: 20px; border-radius: 18px; box-shadow: 0 4px 10px rgba(0,0,0,0.05);">
          <!-- HIGHLIGHT THE URL BELOW and click "Upload Image from Computer" -->
          <img src="https://placehold.co/150x200?text=Upload+Watch+Image" alt="Watch" style="max-width: 100%; height: auto;">
          <p style="color: #bf4800; font-size: 11px; font-weight: 600; margin: 10px 0 2px;">&nbsp;</p>
          <h3 style="margin: 0 0 5px; font-size: 16px;">Smart Watch 10</h3>
          <p style="font-size: 11px; color: #86868b;">From Rs.3575/mo.<br>or Rs.46900*</p>
          <a href="${url}" style="display: inline-block; background-color: #0071e3; color: #ffffff; text-decoration: none; padding: 6px 14px; border-radius: 14px; font-size: 12px; margin-top: 10px;">Buy</a>
        </div>
      </td>
    </tr>
  </table>
</div>`
  },
  {
    name: 'Template 2',
    icon: '🏦',
    generateHtml: (url) => `<div style="font-family: Arial, sans-serif; color: #333333; line-height: 1.5; padding: 15px; text-align: left;">
  <p style="font-size: 13px;">Dear Cardholder,</p>
  <p style="font-size: 13px;">This is to inform you that,</p>
  
  <div style="background-color: #a67329; color: #ffffff; padding: 15px; margin: 20px 0; font-size: 13px;">
    Rs.634.00 spent on your Credit Card ending 7793 at SUPERMARKET on 05/05/26. Trxn. not done by you? Report at <a href="${url}" style="color: #66ccff;">${url}</a>. If you have not authorized this transaction please contact the helpline at 1860 180 1290.
  </div>
  
  <p style="font-size: 12px; color: #555;"><strong>Safe Banking Tip:</strong> Never share your Card Number, CVV, PIN, OTP, Internet Banking User ID, Password or URN with anyone. Sharing these details can lead to unauthorized access to your account.</p>
  
  <div style="border: 1px solid #cccccc; border-radius: 12px; padding: 15px; margin-top: 25px; display: table; width: 100%; background: #ffffff;">
    <div style="display: table-cell; vertical-align: middle; width: 60px; padding-right: 15px;">
      <!-- HIGHLIGHT THE URL BELOW and click "Upload Image from Computer" -->
      <img src="https://placehold.co/60x60?text=Offer" alt="Offer" style="max-width: 100%; border-radius: 50%;">
    </div>
    <div style="display: table-cell; vertical-align: middle;">
      <p style="margin: 0; font-size: 12px;">Exclusive offer! Now convert your purchases of <strong>Rs.200</strong> and above into <strong>Easy EMIs</strong>. Minimum Booking Amount: <strong>Rs.2500</strong>. To know more, <a href="${url}" style="color: #0066cc; text-decoration: underline;">Click here</a></p>
    </div>
  </div>
  
  <p style="margin-top: 25px; font-size: 13px;">Warm regards,<br><br>Customer Service</p>
</div>`
  },
  {
    name: 'Template 3',
    icon: '📰',
    generateHtml: (url) => `<div style="font-family: Arial, sans-serif; color: #333; padding: 20px; background: #f9f9f9; text-align: left;">
  <div style="background: #ffffff; border-top: 4px solid #4f46e5; padding: 25px; border-radius: 0 0 8px 8px;">
    <h2 style="color: #4f46e5; margin-top: 0; font-size: 20px;">Monthly Newsletter</h2>
    <!-- HIGHLIGHT THE URL BELOW and click "Upload Image from Computer" -->
    <img src="https://placehold.co/500x200?text=Upload+Banner+Image" style="width: 100%; border-radius: 8px; margin-bottom: 20px;" />
    <h3 style="margin-bottom: 10px; font-size: 16px;">Latest Updates from Our Team</h3>
    <p style="color: #555; line-height: 1.6; font-size: 13px;">We have some exciting news to share with you this month. Our new product line is finally launching, and we want you to be the first to know.</p>
    <a href="${url}" style="display: inline-block; background: #4f46e5; color: #fff; padding: 8px 16px; text-decoration: none; border-radius: 4px; margin-top: 10px; font-size: 12px; font-weight: bold;">Read Full Story</a>
  </div>
</div>`
  },
  {
    name: 'Template 4',
    icon: '📅',
    generateHtml: (url) => `<div style="font-family: Arial, sans-serif; text-align: center; padding: 40px 20px; background-color: #111827; color: #ffffff; border-radius: 12px;">
  <h4 style="color: #10b981; letter-spacing: 2px; text-transform: uppercase; margin: 0; font-size: 12px;">You're Invited</h4>
  <h1 style="font-size: 28px; margin: 10px 0;">Annual Tech Conference 2026</h1>
  <p style="font-size: 14px; color: #9ca3af; margin-bottom: 30px;">Join industry leaders for an exclusive day of networking and insights.</p>
  
  <div style="background: #1f2937; display: inline-block; padding: 15px 30px; border-radius: 8px; margin-bottom: 30px;">
    <h2 style="margin: 0; color: #10b981; font-size: 20px;">May 15th, 2026</h2>
    <p style="margin: 5px 0 0; color: #d1d5db; font-size: 13px;">10:00 AM - 4:00 PM EST</p>
  </div>
  <br>
  <a href="${url}" style="display: inline-block; background: #10b981; color: #ffffff; text-decoration: none; font-size: 14px; font-weight: bold; padding: 12px 24px; border-radius: 30px;">Reserve Your Seat</a>
</div>`
  }
];

// ─── Local-storage helpers ────────────────────────────────────────────────────
const loadBrand = () => {
  try {
    const s = localStorage.getItem(BRAND_STORAGE_KEY);
    return s ? { ...DEFAULT_BRAND, ...JSON.parse(s) } : DEFAULT_BRAND;
  } catch { return DEFAULT_BRAND; }
};
const saveBrand = (b) => {
  try { localStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(b)); } catch {}
};

// ─── Inline email preview ─────────────────────────────────────────────────────
const EmailPreview = ({
  subject, message, imageUrl, brandName,
  includeDemoSection, includeLogo, includeGreeting, includeWebsiteButton,
}) => {
  const paras = (message || '').split('\n').map(l => l.trim()).filter(Boolean);
  const isHtml = /<[a-z][\s\S]*>/i.test(message || '');

  return (
    <div style={{ fontFamily: 'Georgia, serif', fontSize: '12px', background: '#f3f4f6', padding: '10px', borderRadius: '10px', height: '100%', overflowY: 'auto' }}>
      {subject && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '6px 10px', marginBottom: '8px', fontSize: '11px' }}>
          <span style={{ fontWeight: 700, color: '#92400e' }}>Subject: </span>
          <span style={{ color: '#78350f' }}>{subject}</span>
        </div>
      )}
      <div style={{ background: '#fff', borderRadius: '10px', overflow: 'hidden', border: '2px solid #ffd700', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>

        {/* Logo (optional) */}
        {includeLogo && (
          <div style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
            <span style={{ fontSize: '10px', fontWeight: 800, color: '#4f46e5', letterSpacing: '2px', textTransform: 'uppercase' }}>{brandName || 'YOUR BRAND'}</span>
          </div>
        )}

        {imageUrl && (
          <div style={{ padding: '0 10px 6px' }}>
            <img src={`${CAMPAIGN_IMAGE_URL}${imageUrl}`} alt="" style={{ width: '100%', borderRadius: '6px', marginTop: '8px' }} />
          </div>
        )}

        <div style={{ padding: '10px 14px' }}>
          {isHtml ? (
            <div dangerouslySetInnerHTML={{ __html: message }} style={{ fontSize: '11px', color: '#4b5563', lineHeight: 1.6 }} />
          ) : (
            <>
              {/* Greeting (optional) */}
              {includeGreeting && (
                <p style={{ fontWeight: 700, marginBottom: '8px', color: '#1f2937', fontSize: '11px' }}>Dear Doctor,</p>
              )}
              {paras.length > 0 ? paras.map((p, i) => (
                <p key={i} style={{ margin: '0 0 7px 0', color: '#4b5563', lineHeight: 1.6, fontSize: '11px' }}>{p}</p>
              )) : (
                <p style={{ color: '#d1d5db', fontStyle: 'italic', fontSize: '11px' }}>Your message will appear here…</p>
              )}
            </>
          )}

          {/* Website Button (optional) */}
          {includeWebsiteButton && (
            <div style={{ textAlign: 'center', margin: '12px 0 6px' }}>
              <span style={{ display: 'inline-block', background: '#4f46e5', color: '#fff', padding: '7px 18px', borderRadius: '20px', fontSize: '10px', fontWeight: 700 }}>Visit Our Website</span>
            </div>
          )}

          {/* Demo Section (optional) */}
          {includeDemoSection && (
            <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '8px', textAlign: 'center', marginTop: '8px' }}>
              <div style={{ fontSize: '9px', fontWeight: 700, color: '#7c3aed', marginBottom: '4px' }}>🎯 EXCLUSIVE OFFER</div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>Book a Product Demo</div>
              <span style={{ display: 'inline-block', background: '#4f46e5', color: '#fff', padding: '5px 14px', borderRadius: '16px', fontSize: '9px', fontWeight: 700 }}>📅 Book My Free Demo</span>
            </div>
          )}
        </div>

        <div style={{ background: '#f9fafb', padding: '8px', textAlign: 'center', fontSize: '10px', color: '#9ca3af', borderTop: '1px solid #f0f0f0' }}>
          © {new Date().getFullYear()} {brandName || 'Mitra'} · All rights reserved
        </div>
      </div>
    </div>
  );
};

// ─── Step indicator ───────────────────────────────────────────────────────────
const Steps = ({ step }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    {[{ n: 1, label: 'Compose' }, { n: 2, label: 'Audience' }, { n: 3, label: 'Review' }].map(({ n, label }, idx) => (
      <React.Fragment key={n}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0,
            background: n < step ? '#10b981' : n === step ? '#4f46e5' : '#e5e7eb',
            color: n <= step ? '#fff' : '#9ca3af', transition: 'all 0.3s',
          }}>
            {n < step ? <Check size={13} /> : n}
          </div>
          <span style={{ fontSize: '12px', fontWeight: n === step ? 700 : 500, color: n === step ? '#4f46e5' : n < step ? '#10b981' : '#9ca3af' }}>
            {label}
          </span>
        </div>
        {idx < 2 && <div style={{ flex: 1, height: 2, background: n < step ? '#10b981' : '#e5e7eb', margin: '0 8px', minWidth: 20 }} />}
      </React.Fragment>
    ))}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const CreateCampaignModal = ({ onClose, onSuccess }) => {
  const [step,        setStep]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [inlineUploading, setInlineUploading] = useState(false); // For inside HTML
  const [groups,      setGroups]      = useState([]);
  const [divisions,   setDivisions]   = useState([]);
  const [errors,      setErrors]      = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [brand,       setBrand]       = useState(loadBrand);
  const [showBrand,   setShowBrand]   = useState(false);

  // AI state
  const [aiOpen,    setAiOpen]    = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError,   setAiError]   = useState('');
  const [aiResult,  setAiResult]  = useState(null);
  const [brief,     setBrief]     = useState('');
  const [campType,  setCampType]  = useState('Product Launch');
  const [altIdx,    setAltIdx]    = useState(-1);

  // Refs
  const fileInputRef   = useRef();
  const inlineImageRef = useRef(); // Trigger file picker for local computer
  const textAreaRef    = useRef();

  const [formData, setFormData] = useState({
    subject:                '',
    message:                '',
    image_url:              '',
    source_collection:      '',
    selected_divisions:     [],
    include_demo_section:   true,
    include_logo:           true,
    include_greeting:       true,
    include_website_button: true,
    website_url:            loadBrand().websiteUrl || 'https://www.ottomed.com/',
  });

  useEffect(() => {
    setFormData(prev => ({ ...prev, website_url: brand.websiteUrl }));
  }, [brand.websiteUrl]);

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    try {
      const res = await emailApi.getGroups();
      setGroups(res.data || []);
    } catch { setGroups([]); }
  };

  const loadDivisions = async (group) => {
    if (!group) return;
    try {
      const res = await emailApi.getDivisions(group);
      setDivisions(res.data || []);
    } catch { setDivisions([]); }
  };

  const handleGroupChange = (g) => {
    setFormData(prev => ({ ...prev, source_collection: g, selected_divisions: [] }));
    loadDivisions(g);
  };

  // Upload main campaign image
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const filename = await emailApi.uploadImage(file);
      setFormData(prev => ({ ...prev, image_url: filename }));
    } catch { setErrors(prev => ({ ...prev, image: 'Upload failed' })); }
    finally { setUploading(false); }
  };

  // Upload an image from local computer to inject directly into HTML
  const handleInlineImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setInlineUploading(true);
    
    try {
      // Secretly uploads the local computer file to Laravel server to get a live URL
      const filename = await emailApi.uploadImage(file);
      const fullUrl = `${CAMPAIGN_IMAGE_URL}${filename}`;
      
      const ta = textAreaRef.current;
      if (ta) {
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const text = formData.message;
        const hasSelection = start !== end;
        
        // Smart Inject: If they highlighted a placeholder URL, just replace the URL.
        // If they just clicked in an empty space, create a full <img> tag for them.
        const injection = hasSelection 
          ? fullUrl 
          : `\n<img src="${fullUrl}" alt="Uploaded Image" style="max-width: 100%; border-radius: 8px;" />\n`;

        const newText = text.substring(0, start) + injection + text.substring(end);
        setFormData(prev => ({ ...prev, message: newText }));
        
        // Return focus to textarea
        setTimeout(() => ta.focus(), 100);
      }
    } catch (err) { 
      alert('Failed to upload image. Make sure it is a valid image file.'); 
    } finally { 
      setInlineUploading(false); 
      e.target.value = null; // reset input
    }
  };

  const updateBrand = (key, value) => {
    const next = { ...brand, [key]: value };
    setBrand(next);
    saveBrand(next);
  };

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    const oldUrl = formData.website_url;
    
    setFormData(prev => {
      let updatedMessage = prev.message;
      if (oldUrl && oldUrl.trim() !== '' && updatedMessage.includes(oldUrl)) {
        updatedMessage = updatedMessage.split(oldUrl).join(newUrl || '#');
      }
      return { ...prev, website_url: newUrl, message: updatedMessage };
    });
  };

  // ── AI generation ────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!brief.trim()) { setAiError('Please describe your campaign first.'); return; }
    setAiLoading(true);
    setAiError('');
    setAiResult(null);
    setAltIdx(-1);
    try {
      const response = await generateCampaignContent({
        brief,
        campaignType: campType,
        tone:         brand.tone,
        brandName:    brand.brandName,
      });
      if (!response.success) throw new Error(response.message || 'Generation failed');
      const result = response.data;
      setAiResult(result);
      setFormData(prev => ({ ...prev, subject: result.subject, message: result.message }));
    } catch (e) {
      setAiError(e.response?.data?.message || e.message || 'Generation failed — please try again.');
    } finally { setAiLoading(false); }
  };

  const applySubjectAlt = (idx) => {
    const alt = aiResult?.subjectAlternatives?.[idx];
    if (!alt) return;
    setAltIdx(idx);
    setFormData(prev => ({ ...prev, subject: alt }));
  };

  const applyOriginalSubject = () => {
    if (!aiResult) return;
    setAltIdx(-1);
    setFormData(prev => ({ ...prev, subject: aiResult.subject }));
  };

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!formData.subject.trim()) e.subject = 'Subject is required';
      if (!formData.message.trim()) e.message = 'Message is required';
    } else if (step === 2) {
      if (!formData.source_collection)              e.source_collection  = 'Select a customer group';
      if (formData.selected_divisions.length === 0) e.selected_divisions = 'Select at least one division';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext     = () => { if (validate()) setStep(s => s + 1); };
  const handleBack     = () => setStep(s => s - 1);
  const toggleDivision = (d) => setFormData(prev => ({
    ...prev,
    selected_divisions: prev.selected_divisions.includes(d)
      ? prev.selected_divisions.filter(x => x !== d)
      : [...prev.selected_divisions, d],
  }));

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const data = { ...formData, website_url: formData.website_url?.trim() || 'https://www.ottomed.com/', brand_name: brand.brandName || '' };
      const res = await emailApi.createCampaign(data);
      onSuccess(res.data);
    } catch (e) {
      setErrors({ submit: e.response?.data?.message || e.message || 'Failed to create campaign' });
    } finally { setLoading(false); }
  };

  // ─── Shared styles ────────────────────────────────────────────────────────
  const inputStyle = (hasErr) => ({
    width: '100%', padding: '9px 12px',
    border: `1.5px solid ${hasErr ? '#ef4444' : '#e5e7eb'}`,
    borderRadius: '8px', fontSize: '14px', outline: 'none',
    background: '#fafafa', transition: 'border-color 0.2s', fontFamily: 'inherit',
  });
  const labelStyle = {
    display: 'block', fontSize: '12px', fontWeight: 700,
    color: '#374151', marginBottom: '5px', letterSpacing: '0.3px',
  };

  // ─── STEP 1: Compose ─────────────────────────────────────────────────────
  const renderStep1 = () => (
    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

      {/* Left column */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Brand Settings */}
        <div style={{ border: '1.5px solid #e5e7eb', borderRadius: '10px', marginBottom: '16px', overflow: 'hidden' }}>
          <button onClick={() => setShowBrand(b => !b)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f9fafb', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', fontWeight: 700, color: '#374151' }}>
              <Settings size={14} color="#6b7280" />
              Brand Settings
              {brand.brandName && <span style={{ background: '#ede9fe', color: '#7c3aed', fontSize: '10px', padding: '1px 7px', borderRadius: '10px', fontWeight: 600 }}>{brand.brandName}</span>}
            </span>
            {showBrand ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showBrand && (
            <div style={{ padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Brand / Company Name</label>
                <input value={brand.brandName} onChange={e => updateBrand('brandName', e.target.value)}
                  placeholder="e.g. Mitra Industries" style={{ ...inputStyle(false), fontSize: '13px' }} />
              </div>
              <div>
                <label style={labelStyle}>Default Website URL</label>
                <input value={brand.websiteUrl} onChange={e => updateBrand('websiteUrl', e.target.value)}
                  placeholder="https://www.ottomed.com/" style={{ ...inputStyle(false), fontSize: '13px' }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Email Tone</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {TONES.map(t => (
                    <button key={t.value} onClick={() => updateBrand('tone', t.value)}
                      style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', border: '1.5px solid', fontFamily: 'inherit', transition: 'all 0.2s',
                        borderColor: brand.tone === t.value ? '#7c3aed' : '#e5e7eb',
                        background:  brand.tone === t.value ? '#ede9fe' : '#fff',
                        color:        brand.tone === t.value ? '#7c3aed' : '#6b7280',
                      }}>
                      {t.emoji} {t.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Saved automatically for all future campaigns.</p>
              </div>
            </div>
          )}
        </div>

        {/* AI Template Creator */}
        <div style={{ border: '1.5px solid #c4b5fd', borderRadius: '10px', marginBottom: '16px', overflow: 'hidden', background: 'linear-gradient(135deg, #faf5ff 0%, #eff6ff 100%)' }}>
          <button onClick={() => setAiOpen(b => !b)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', fontWeight: 700, color: '#5b21b6' }}>
              <Sparkles size={14} color="#7c3aed" />
              AI Template Creator
              <span style={{ background: '#7c3aed', color: '#fff', fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, letterSpacing: '0.5px' }}>AI</span>
            </span>
            {aiOpen ? <ChevronUp size={14} color="#7c3aed" /> : <ChevronDown size={14} color="#7c3aed" />}
          </button>

          {aiOpen && (
            <div style={{ padding: '0 14px 14px' }}>

              {/* Campaign type chips */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {CAMPAIGN_TYPES.map(t => (
                  <button key={t.label} onClick={() => setCampType(t.label)}
                    style={{ padding: '5px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      border: '1.5px solid', transition: 'all 0.15s',
                      borderColor: campType === t.label ? '#7c3aed' : '#ddd6fe',
                      background:  campType === t.label ? '#7c3aed' : '#fff',
                      color:        campType === t.label ? '#fff'    : '#6b21a8',
                    }}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>

              {/* Brief input */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ ...labelStyle, color: '#5b21b6' }}>Describe your campaign in one sentence</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={brief} onChange={e => setBrief(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                    placeholder="e.g. Launching the new OttoMed Ultra pulse oximeter for ICU use"
                    style={{ ...inputStyle(false), flex: 1, fontSize: '13px', background: '#fff' }} />
                  <button onClick={handleGenerate} disabled={aiLoading || !brief.trim()}
                    style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                      background: aiLoading ? '#c4b5fd' : '#7c3aed', color: '#fff',
                      fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '5px',
                      opacity: !brief.trim() ? 0.5 : 1, fontFamily: 'inherit', flexShrink: 0,
                    }}>
                    {aiLoading
                      ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Writing…</>
                      : <><Wand2 size={13} /> Generate</>}
                  </button>
                </div>
              </div>

              {/* Error */}
              {aiError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', color: '#dc2626', marginBottom: '8px' }}>
                  ⚠ {aiError}
                </div>
              )}

              {/* Results */}
              {aiResult && (
                <div style={{ background: '#fff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#7c3aed' }}>✓ Generated — applied to your email</span>
                    <button onClick={handleGenerate}
                      style={{ fontSize: '11px', color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontFamily: 'inherit' }}>
                      <RotateCcw size={11} /> Regenerate
                    </button>
                  </div>

                  {/* Subject alternatives */}
                  {aiResult.subjectAlternatives?.length > 0 && (
                    <div>
                      <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '5px' }}>Subject line alternatives — click to use:</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <button onClick={applyOriginalSubject}
                          style={{ textAlign: 'left', padding: '6px 10px', borderRadius: '6px', border: '1px solid', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px', transition: 'all 0.15s',
                            borderColor: altIdx === -1 ? '#7c3aed' : '#e5e7eb',
                            background:  altIdx === -1 ? '#ede9fe' : '#f9fafb',
                            color:        altIdx === -1 ? '#5b21b6' : '#374151',
                            fontWeight:   altIdx === -1 ? 700 : 400 }}>
                          {altIdx === -1 && '✓ '}{aiResult.subject}
                        </button>
                        {aiResult.subjectAlternatives.map((s, i) => (
                          <button key={i} onClick={() => applySubjectAlt(i)}
                            style={{ textAlign: 'left', padding: '6px 10px', borderRadius: '6px', border: '1px solid', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px', transition: 'all 0.15s',
                              borderColor: altIdx === i ? '#7c3aed' : '#e5e7eb',
                              background:  altIdx === i ? '#ede9fe' : '#f9fafb',
                              color:        altIdx === i ? '#5b21b6' : '#374151',
                              fontWeight:   altIdx === i ? 700 : 400 }}>
                            {altIdx === i && '✓ '}{s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pro tip */}
                  {aiResult.proTip && (
                    <div style={{ marginTop: '8px', display: 'flex', gap: '6px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px', padding: '7px 10px' }}>
                      <Lightbulb size={12} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span style={{ fontSize: '11px', color: '#92400e' }}>{aiResult.proTip}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Subject */}
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Subject Line <span style={{ color: '#ef4444' }}>*</span></label>
          <input type="text" value={formData.subject}
            onChange={e => { setFormData(prev => ({ ...prev, subject: e.target.value })); setAltIdx(-1); }}
            placeholder="Enter a compelling subject line" style={inputStyle(!!errors.subject)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
            {errors.subject ? <span style={{ fontSize: '11px', color: '#ef4444' }}>{errors.subject}</span> : <span />}
            <span style={{ fontSize: '11px', color: formData.subject.length > 60 ? '#ef4444' : '#9ca3af' }}>
              {formData.subject.length}/60
            </span>
          </div>
        </div>

        {/* Website URL (Moved up so users fill it BEFORE selecting a template) */}
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}><Globe size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />"Visit Our Website" URL</label>
          <input type="text" value={formData.website_url}
            onChange={handleUrlChange}
            placeholder="https://www.ottomed.com/"
            style={{ ...inputStyle(false), fontFamily: 'monospace', fontSize: '12px' }} />
          <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>
            Set this URL. Any HTML template you select below will automatically use this link for its buttons.
          </p>
        </div>

        {/* Message Input with Templates, Images, and HTML Upload */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Email Message <span style={{ color: '#ef4444' }}>*</span></label>
            
            {/* Tool Buttons */}
            <div style={{ display: 'flex', gap: '6px' }}>
              
              {/* Insert Image Button */}
              <input 
                type="file" 
                accept="image/*" 
                ref={inlineImageRef} 
                style={{ display: 'none' }}
                onChange={handleInlineImageUpload} 
              />
              <button 
                type="button"
                disabled={inlineUploading}
                onClick={() => inlineImageRef.current.click()}
                title="Highlight an image URL in the code below and click this to replace it with a file from your computer!"
                style={{ fontSize: '11px', padding: '4px 10px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <UploadCloud size={12} /> {inlineUploading ? 'Uploading...' : 'Upload Image from Computer'}
              </button>

        {/* Upload HTML Button - Updated Version */}
          <input
              type="file"
              accept=".html,.htm"
              id="html-upload"
              style={{ display: 'none' }}
              onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  const reader = new FileReader();
                  reader.onload = (evt) => {
                      try {
                          let htmlContent = evt.target.result;
                          
                          // Log the HTML content length for debugging
                          console.log('HTML file loaded, length:', htmlContent.length);
                          
                          // Extract just the body content if it's a full HTML document
                          const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                          if (bodyMatch && bodyMatch[1]) {
                              htmlContent = bodyMatch[1].trim();
                              console.log('Extracted body content, length:', htmlContent.length);
                          }
                          
                          // Clean up any script tags for security
                          htmlContent = htmlContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
                          
                          // Remove any dangerous attributes or tags (basic sanitization)
                          htmlContent = htmlContent.replace(/on\w+="[^"]*"/gi, '');
                          
                          // Update the form data with the HTML content
                          setFormData(prev => ({ 
                              ...prev, 
                              message: htmlContent 
                          }));
                          
                          // Show success message
                          alert('HTML template loaded successfully! You can now preview and send it.');
                      } catch (err) {
                          console.error('Error processing HTML file:', err);
                          alert('Error processing HTML file. Please make sure it\'s a valid HTML file.');
                      }
                  };
                  reader.onerror = () => {
                      console.error('Error reading file');
                      alert('Error reading HTML file. Please try again.');
                  };
                  reader.readAsText(file);
                  e.target.value = null; // Reset input
              }}
          />

          <button
              type="button"
              onClick={() => document.getElementById('html-upload').click()}
              style={{ fontSize: '11px', padding: '4px 10px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}
          >
              <FileText size={12} /> Upload .HTML File
          </button>

            </div>
          </div>

          {/* Predefined Template Selector */}
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0', padding: '8px 12px', borderBottom: 'none', display: 'flex', gap: '8px', overflowX: 'auto', whiteSpace: 'nowrap', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, marginRight: '4px' }}>Templates:</span>
            {PREDEFINED_TEMPLATES.map((tmpl, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  if (formData.message && !window.confirm('This will overwrite your current message. Continue?')) return;
                  // Get the current URL to inject into the template (fallback to # if empty)
                  const link = formData.website_url || '#';
                  setFormData(prev => ({ ...prev, message: tmpl.generateHtml(link) }));
                }}
                style={{
                  padding: '4px 10px', borderRadius: '14px', border: '1px solid #d1d5db', background: '#fff', 
                  fontSize: '11px', fontWeight: 600, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151'; }}
              >
                {tmpl.icon} {tmpl.name}
              </button>
            ))}
          </div>

          {/* Text Area */}
          <textarea 
            ref={textAreaRef}
            value={formData.message} 
            onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
            rows={8} 
            placeholder="Write your email body here, paste custom HTML code, or choose a template above."
            style={{ 
              ...inputStyle(!!errors.message), 
              borderRadius: '0 0 8px 8px', // Flatten top corners to attach to template bar
              resize: 'vertical', 
              lineHeight: 1.6, 
              fontFamily: /<[a-z][\s\S]*>/i.test(formData.message || '') ? 'monospace' : 'inherit' 
            }} 
          />
          {errors.message && <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '3px' }}>{errors.message}</p>}
        </div>

        {/* Global Campaign Image upload (Displayed above message) */}
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Global Campaign Image <span style={{ color: '#9ca3af', fontWeight: 500 }}>(optional)</span></label>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px 0' }}>This adds an image to the very top of your email. (Leave blank if using an HTML Template).</p>
          {formData.image_url ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={`${CAMPAIGN_IMAGE_URL}${formData.image_url}`} alt="Preview"
                style={{ maxHeight: '120px', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'block' }} />
              <button onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={12} />
              </button>
            </div>
          ) : (
            <div onClick={() => fileInputRef.current.click()}
              style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '20px', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.background = '#faf5ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fafafa'; }}>
              <ImageIcon size={24} color="#9ca3af" style={{ margin: '0 auto 6px' }} />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{uploading ? 'Uploading…' : 'Click to upload a global campaign image'}</p>
              <input ref={fileInputRef} type="file" onChange={handleImageUpload} style={{ display: 'none' }} accept="image/*" />
            </div>
          )}
          {errors.image && <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '3px' }}>{errors.image}</p>}
        </div>

        {/* ── Section Toggles ── */}
        {[
          { key: 'include_logo',           label: 'Include Company Logo?',             desc: 'Shows the Mitra Industries logo at the top of the email.' },
          { key: 'include_greeting',       label: 'Include "Dear Doctor" Greeting?',   desc: 'Adds a personalised "Dear [Name]," salutation at the top.' },
          { key: 'include_website_button', label: 'Include "Visit Our Website" Button?',desc: 'Adds the blue Visit Our Website button below the message.' },
          { key: 'include_demo_section',   label: 'Include Demo Booking Section?',     desc: 'Adds the "📅 Book My Free Demo" block at the bottom.' },
        ].map(({ key, label, desc }) => (
          <div key={key} style={{ border: '1.5px solid #e5e7eb', borderRadius: '10px', padding: '12px', background: '#fafafa', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#374151', margin: '0 0 2px' }}>{label}</p>
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>{desc}</p>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {[
                  { val: true,  label: '✓ Yes', activeColor: '#10b981' },
                  { val: false, label: '✕ No',  activeColor: '#ef4444' },
                ].map(b => (
                  <button key={String(b.val)} onClick={() => setFormData(prev => ({ ...prev, [key]: b.val }))}
                    style={{ padding: '5px 14px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, fontFamily: 'inherit', transition: 'all 0.2s',
                      background: formData[key] === b.val ? b.activeColor : '#e5e7eb',
                      color:       formData[key] === b.val ? '#fff'       : '#6b7280',
                    }}>
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Right column – live preview */}
      <div style={{ width: '240px', flexShrink: 0 }}>
        <div style={{ position: 'sticky', top: 0 }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Eye size={12} /> Live Preview
          </p>
          <EmailPreview
            subject={formData.subject}
            message={formData.message}
            imageUrl={formData.image_url}
            brandName={brand.brandName}
            includeDemoSection={formData.include_demo_section}
            includeLogo={formData.include_logo}
            includeGreeting={formData.include_greeting}
            includeWebsiteButton={formData.include_website_button}
          />
        </div>
      </div>
    </div>
  );

  // ─── STEP 2: Audience ─────────────────────────────────────────────────────
  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={labelStyle}><Users size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />Customer Group <span style={{ color: '#ef4444' }}>*</span></label>
        <select value={formData.source_collection} onChange={e => handleGroupChange(e.target.value)}
          style={{ ...inputStyle(!!errors.source_collection), cursor: 'pointer' }}>
          <option value="">Select a customer group…</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        {errors.source_collection && <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '3px' }}>{errors.source_collection}</p>}
      </div>

      {formData.source_collection && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <label style={labelStyle}>
              Select Divisions <span style={{ color: '#ef4444' }}>*</span>
              {formData.selected_divisions.length > 0 && (
                <span style={{ marginLeft: '8px', background: '#ede9fe', color: '#7c3aed', fontSize: '10px', padding: '2px 7px', borderRadius: '10px', fontWeight: 700 }}>
                  {formData.selected_divisions.length} selected
                </span>
              )}
            </label>
            {divisions.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setFormData(prev => ({ ...prev, selected_divisions: divisions.map(d => typeof d === 'string' ? d : d.division) }))}
                  style={{ fontSize: '12px', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                  Select all
                </button>
                {formData.selected_divisions.length > 0 && (
                  <button onClick={() => setFormData(prev => ({ ...prev, selected_divisions: [] }))}
                    style={{ fontSize: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>

          {divisions.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', maxHeight: '220px', overflowY: 'auto', padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: '10px', background: '#fafafa' }}>
              {divisions.map(div => {
                const val = typeof div === 'string' ? div : div.division;
                const sel = formData.selected_divisions.includes(val);
                return (
                  <button key={val} onClick={() => toggleDivision(val)}
                    style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                      border: '1.5px solid', borderColor: sel ? '#4f46e5' : '#d1d5db',
                      background: sel ? '#4f46e5' : '#fff', color: sel ? '#fff' : '#374151' }}>
                    {sel && '✓ '}{val}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px', border: '1.5px dashed #e5e7eb', borderRadius: '10px', color: '#9ca3af', fontSize: '13px' }}>
              No divisions found for this group
            </div>
          )}
          {errors.selected_divisions && <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '5px' }}>{errors.selected_divisions}</p>}
        </div>
      )}

      {!formData.source_collection && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af', fontSize: '13px' }}>
          <Users size={32} color="#d1d5db" style={{ margin: '0 auto 10px', display: 'block' }} />
          Select a customer group above to choose your audience
        </div>
      )}
    </div>
  );

  // ─── STEP 3: Review ───────────────────────────────────────────────────────
  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        {[
          { label: 'Subject',        value: formData.subject,              span: 2 },
          { label: 'Target Group',   value: formData.source_collection },
          { label: 'Divisions',      value: `${formData.selected_divisions.length} selected` },
          { label: 'Website URL',      value: formData.website_url || 'Default', span: 2, mono: true },
          { label: 'Logo',             value: formData.include_logo            ? '✓ Included' : '✕ Not included', color: formData.include_logo            ? '#10b981' : '#ef4444' },
          { label: 'Greeting',         value: formData.include_greeting        ? '✓ Included' : '✕ Not included', color: formData.include_greeting        ? '#10b981' : '#ef4444' },
          { label: 'Website Button',   value: formData.include_website_button  ? '✓ Included' : '✕ Not included', color: formData.include_website_button  ? '#10b981' : '#ef4444' },
          { label: 'Demo Section',     value: formData.include_demo_section    ? '✓ Included' : '✕ Not included', color: formData.include_demo_section    ? '#10b981' : '#ef4444' },
          { label: 'Campaign Image',   value: formData.image_url               ? '✓ Uploaded' : 'None' },
        ].map(({ label, value, span, mono, color }) => (
          <div key={label} style={{ gridColumn: span === 2 ? '1 / -1' : 'auto', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px' }}>
            <p style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 3px' }}>{label}</p>
            <p style={{ fontSize: '13px', fontWeight: 700, color: color || '#111827', margin: 0, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value || '—'}</p>
          </div>
        ))}
      </div>

      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
        <p style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px' }}>Message Preview</p>
        <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.6, margin: 0 }}>
          {/<[a-z][\s\S]*>/i.test(formData.message) 
            ? "[HTML Template Selected]" 
            : formData.message.substring(0, 200) + (formData.message.length > 200 ? '…' : '')}
        </p>
      </div>

      {formData.selected_divisions.length > 0 && (
        <div>
          <p style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Selected Divisions</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {formData.selected_divisions.slice(0, 8).map(d => (
              <span key={d} style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '12px' }}>{d}</span>
            ))}
            {formData.selected_divisions.length > 8 && (
              <span style={{ background: '#e5e7eb', color: '#6b7280', fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '12px' }}>+{formData.selected_divisions.length - 8} more</span>
            )}
          </div>
        </div>
      )}

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <AlertCircle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
        <div>
          <p style={{ fontSize: '13px', fontWeight: 700, color: '#92400e', margin: '0 0 3px' }}>Ready to create?</p>
          <p style={{ fontSize: '12px', color: '#b45309', margin: 0 }}>This saves the campaign as a draft. You can review and send it from the dashboard.</p>
        </div>
      </div>

      {errors.submit && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#dc2626' }}>
          {errors.submit}
        </div>
      )}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,10,30,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: step === 1 ? '980px' : '620px', maxHeight: '94vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)', transition: 'max-width 0.3s ease' }}>

          {/* Header */}
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(to right, #faf5ff, #f5f3ff)', flexShrink: 0 }}>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 800, color: '#1e1b4b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={18} color="#7c3aed" fill="#7c3aed" /> Create New Campaign
              </h2>
              <p style={{ fontSize: '12px', color: '#7c3aed', margin: '2px 0 0', fontWeight: 500 }}>
                {step === 1 ? 'Compose your email' : step === 2 ? 'Choose your audience' : 'Review and confirm'}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={() => setShowPreview(b => !b)}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: showPreview ? '#ede9fe' : '#fff', color: showPreview ? '#7c3aed' : '#6b7280', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'inherit' }}>
                {showPreview ? <EyeOff size={13} /> : <Eye size={13} />} Preview
              </button>
              <button onClick={onClose}
                style={{ padding: '6px', borderRadius: '8px', border: 'none', background: '#f3f4f6', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={18} color="#6b7280" />
              </button>
            </div>
          </div>

          {/* Step indicator */}
          <div style={{ padding: '14px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
            <Steps step={step} />
          </div>

          {/* Mobile preview toggle */}
          {showPreview && step === 1 && (
            <div style={{ padding: '12px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0, background: '#fafafa' }}>
              <EmailPreview
                subject={formData.subject}
                message={formData.message}
                imageUrl={formData.image_url}
                brandName={brand.brandName}
                includeDemoSection={formData.include_demo_section}
                includeLogo={formData.include_logo}
                includeGreeting={formData.include_greeting}
                includeWebsiteButton={formData.include_website_button}
              />
            </div>
          )}

          {/* Content */}
          <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </div>

          {/* Footer */}
          <div style={{ padding: '14px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa', flexShrink: 0 }}>
            <button onClick={step > 1 ? handleBack : onClose}
              style={{ padding: '9px 18px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {step > 1 ? '← Back' : 'Cancel'}
            </button>
            <button onClick={step < 3 ? handleNext : handleSubmit} disabled={loading || uploading}
              style={{ padding: '9px 24px', borderRadius: '8px', border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '7px', fontFamily: 'inherit',
                background: loading ? '#c4b5fd' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                boxShadow: '0 4px 12px rgba(124,58,237,0.3)', transition: 'all 0.2s' }}>
              {loading
                ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Creating…</>
                : step < 3
                  ? <>Continue <ChevronRight size={15} /></>
                  : <><FileText size={14} /> Create Campaign</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCampaignModal;